/**
 * Export Controller
 * Handles export settings UI and export execution workflow
 *
 * Refactored to use centralized configuration from lib/export-config.js
 */

import { playlistState, lyricsState, exportState, managers } from './state.js';
import { elements } from './dom-cache.js';
import { VideoExporter } from '../exporter.js';
import { entitlementService } from '../entitlements/entitlement-service.js';
import { featureGate } from '../entitlements/feature-gate.js';
import { buildControlsUI, recalculateLyricsScrollParams, updateVisualizerGridSelection } from './visualizer-controls.js';
import { exitLiveMode, enterLiveMode } from './live-mode.js';
import { uiState, playbackState } from './state.js';
import { cancelDurationInitialization, resumeDurationInitialization } from './file-handler.js';
import { pausePlayback, startPlayback, ensureVisualizerReady } from './playback.js';
import { pausePlaylistAnimations, resumePlaylistAnimations } from './playlist.js';
import { pauseThumbnailObserver, resumeThumbnailObserver } from './visualizer-controls.js';
import { perfMonitor } from './performance-monitor.js';
import { TIMEOUT_CONFIG, getPlatformCleanupDelay } from '../lib/export-config.js';
import { trackExportInitiated, trackExportCompleted } from './analytics/event-tracker.js';

// Track previous values for reverting on feature gate rejection
let previousResolution = null;
let previousFps = null;

/**
 * Set up a segmented control to sync with a hidden select.
 * When a button is clicked, updates the active class and syncs the hidden select value.
 * @param {HTMLElement} control - The segmented control container
 * @param {HTMLSelectElement} select - The hidden select element
 */
function setupSegmentedControlSync(control, select) {
  if (!control || !select) return;

  control.querySelectorAll('.segment').forEach(btn => {
    btn.addEventListener('click', () => {
      // Update active state on all buttons
      control.querySelectorAll('.segment').forEach(b => b.classList.remove('active'));
      btn.classList.add('active');

      // Update hidden select and trigger change event
      const value = btn.dataset.value;
      if (select.value !== value) {
        select.value = value;
        select.dispatchEvent(new Event('change'));
      }
    });
  });
}

/**
 * Sync segmented control visual state to match hidden select value.
 * Used after a revert or when the select value changes programmatically.
 * @param {HTMLElement} control - The segmented control container
 * @param {string} value - The value to set active
 */
function syncSegmentedControlToSelect(control, value) {
  if (!control) return;
  control.querySelectorAll('.segment').forEach(btn => {
    btn.classList.toggle('active', btn.dataset.value === value);
  });
}

/**
 * Pre-download audio for streamed (Suno) tracks before export begins.
 * Stores compressed MP3 data as rawAudioData (ArrayBuffer) on each track.
 * This ensures the render phase runs entirely offline with no network dependency.
 *
 * @param {Array} tracks - Playlist items to check for streamed audio
 * @param {Object} options
 * @param {Function} options.onProgress - Called with (current, total) for each download
 * @param {Function} options.isAborted - Returns true if export was aborted
 */
async function preDownloadStreamedAudio(tracks, { onProgress, isAborted }) {
  const toDownload = tracks.filter(t =>
    t.isStreamed && t.streamUrl && !t.audioBuffer && !t.rawAudioData
  );

  if (toDownload.length === 0) return;

  console.log(`[Export] Pre-downloading audio for ${toDownload.length} streamed track(s)`);

  const abortController = new AbortController();

  for (let i = 0; i < toDownload.length; i++) {
    if (isAborted()) {
      abortController.abort();
      console.log('[Export] Pre-download aborted');
      return;
    }

    const track = toDownload[i];
    if (onProgress) onProgress(i + 1, toDownload.length);

    try {
      const response = await fetch(track.streamUrl, { signal: abortController.signal });
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      track.rawAudioData = await response.arrayBuffer();
      console.log(`[Export] Downloaded "${track.name}" (${(track.rawAudioData.byteLength / 1024).toFixed(0)} KB)`);
    } catch (err) {
      if (err.name === 'AbortError') {
        console.log('[Export] Pre-download fetch aborted');
        return;
      }
      throw new Error(`Failed to download "${track.name || 'unknown'}": ${err.message}`);
    }
  }

  console.log(`[Export] All ${toDownload.length} streamed track(s) downloaded`);
}

/**
 * Show the inline export controls (replaces transport controls during export)
 * Called when export starts
 */
function showExportStatusBar() {
  // Add exporting class to transport bar to swap controls
  if (elements.transportBar) {
    elements.transportBar.classList.add('exporting');
  }

  // Initialize Lucide icons in the export controls
  if (elements.transportExportControls && window.lucide) {
    window.lucide.createIcons({ nodes: [elements.transportExportControls] });
  }

  // Disable the sidebar export button during export
  if (elements.exportIconBtn) {
    elements.exportIconBtn.disabled = true;
    elements.exportIconBtn.classList.add('disabled');
  }
}

/**
 * Hide the inline export controls (restores transport controls)
 * Called when export ends
 */
function hideExportStatusBar() {
  // Remove exporting class to restore normal transport controls
  if (elements.transportBar) {
    elements.transportBar.classList.remove('exporting');
  }

  // Re-enable the sidebar export button
  if (elements.exportIconBtn) {
    elements.exportIconBtn.disabled = false;
    elements.exportIconBtn.classList.remove('disabled');
  }
}

/**
 * Update the inline export status with current progress
 */
function updateExportStatusBar({ trackName, progress, fps, eta, size }) {
  if (trackName !== undefined && elements.exportInlineTrackName) {
    elements.exportInlineTrackName.textContent = trackName;
  }

  if (progress !== undefined) {
    const percent = Math.round(progress * 100);
    if (elements.exportInlineProgressFill) {
      elements.exportInlineProgressFill.style.width = percent + '%';
    }
    if (elements.exportInlineProgressText) {
      elements.exportInlineProgressText.textContent = percent + '%';
    }
  }

  if (fps !== undefined && elements.exportInlineFps) {
    const fpsSpan = elements.exportInlineFps.querySelector('span');
    if (fpsSpan) {
      fpsSpan.textContent = `${fps.toFixed(1)}`;
    }
  }

  if (eta !== undefined && elements.exportInlineEta) {
    const etaSpan = elements.exportInlineEta.querySelector('span');
    if (etaSpan) {
      etaSpan.textContent = formatEta(eta);
    }
  }

  if (size !== undefined && elements.exportInlineSize) {
    const sizeSpan = elements.exportInlineSize.querySelector('span');
    if (sizeSpan) {
      const mb = (size / (1024 * 1024)).toFixed(1);
      sizeSpan.textContent = `${mb} MB`;
    }
  }
}

/**
 * Reset the inline export status to initial state
 */
function resetExportStatusBar() {
  updateExportStatusBar({
    trackName: 'Preparing...',
    progress: 0,
    fps: 0,
    eta: -1,
    size: 0
  });
  // Re-enable stop button for next export
  if (elements.exportInlineStopBtn) {
    elements.exportInlineStopBtn.disabled = false;
  }
}

/**
 * Setup the inline export stop button handler
 * Called during initialization
 */
export function setupExportStatusBar() {
  if (elements.exportInlineStopBtn) {
    elements.exportInlineStopBtn.addEventListener('click', () => {
      if (exportState.currentExporter) {
        exportState.exportWasAborted = true;
        exportState.currentExporter.abort();
        elements.exportInlineStopBtn.disabled = true;
        if (elements.exportInlineTrackName) {
          elements.exportInlineTrackName.textContent = 'Stopping...';
        }
      } else if (exportState.isExporting) {
        // Pre-download phase: no exporter yet, abort flag stops the download loop
        exportState.exportWasAborted = true;
        elements.exportInlineStopBtn.disabled = true;
        if (elements.exportInlineTrackName) {
          elements.exportInlineTrackName.textContent = 'Stopping...';
        }
      }
    });
  }
}

/**
 * Run pre-export diagnostics to detect potential performance issues.
 * Logs warnings for common problems that can cause export slowdowns.
 * @returns {string[]} Array of warning messages (empty if all good)
 */
function runPreExportDiagnostics() {
  const warnings = [];

  // Check for multiple Three.js instances (major performance killer for WebGPU)
  // Three.js sets a flag when it's imported multiple times
  if (typeof window !== 'undefined') {
    // Check for the warning that Three.js emits
    const threeRevision = window.THREE?.REVISION;
    if (threeRevision) {
      console.log(`[Export Diagnostics] Three.js revision: ${threeRevision}`);
    }
  }

  // Check if live mode animation is still running
  if (uiState.isLiveMode) {
    warnings.push('Live mode is still active - should be stopped before export');
  }

  // Check if playback animation is still running
  if (playbackState.animationId) {
    warnings.push('Playback animation loop is still running');
  }

  // Check if playback is in progress
  if (playbackState.isPlaying) {
    warnings.push('Audio playback is still active');
  }

  // Log diagnostics summary
  if (warnings.length > 0) {
    console.warn('[Export Diagnostics] Potential performance issues detected:');
    warnings.forEach(w => console.warn(`  - ${w}`));
  } else {
    console.log('[Export Diagnostics] Pre-export checks passed');
  }

  return warnings;
}

/**
 * Update the playlist summary section in the export panel
 * Shows counts for songs to export, excluded, and missing lyrics
 */
export function updateExportPlaylistSummary() {
  const { playlist } = playlistState;

  const includedTracks = playlist.filter(t => t.included);
  const excludedTracks = playlist.filter(t => !t.included);
  const missingLyricsTracks = includedTracks.filter(t => !t.lyrics);

  // Update songs to export count
  if (elements.exportSongsToExport) {
    const countEl = elements.exportSongsToExport.querySelector('.export-stat-count');
    if (countEl) {
      countEl.textContent = includedTracks.length;
    }
  }

  // Update excluded songs count
  if (elements.exportSongsExcluded) {
    const countEl = elements.exportSongsExcluded.querySelector('.export-stat-count');
    if (countEl) {
      countEl.textContent = excludedTracks.length;
    }
    // Hide if no excluded songs
    elements.exportSongsExcluded.style.display = excludedTracks.length > 0 ? 'inline-flex' : 'none';
  }

  // Update missing lyrics count (warning badge)
  if (elements.exportSongsMissingLyrics) {
    const countEl = elements.exportSongsMissingLyrics.querySelector('.export-stat-count');
    if (countEl) {
      countEl.textContent = missingLyricsTracks.length;
    }
    // Only show if there are included tracks with missing lyrics
    elements.exportSongsMissingLyrics.style.display = missingLyricsTracks.length > 0 ? 'inline-flex' : 'none';
  }
}

/**
 * Format ETA for export progress
 */
export function formatEta(seconds) {
  if (!isFinite(seconds) || seconds < 0) return '--:--';
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}

/**
 * Probe codec support for current settings
 */
export async function probeCodecSupport() {
  const [exportWidth, exportHeight] = elements.resolutionSelect.value.split('x').map(Number);
  const fps = parseInt(elements.fpsSelect.value, 10);
  const codecPreset = elements.exportCodecSelect.value;
  const bitrate = parseInt(elements.exportBitrateSelect.value, 10);
  const codecMap = {
    hevc: 'hvc1.1.6.L93.B0',
    baseline: 'avc1.42E01E',
    main: 'avc1.4D4028',
    auto: null
  };
  const codecString = codecMap[codecPreset] || null;
  if (!navigator.mediaCapabilities?.encodingInfo || !codecString) {
    console.log('[Export] MediaCapabilities probe skipped (not supported or auto codec)');
    return;
  }
  const mcTypes = ['record', 'webrtc', 'file'];
  let probed = false;
  for (const mcType of mcTypes) {
    try {
      const info = await navigator.mediaCapabilities.encodingInfo({
        type: mcType,
        video: {
          contentType: `video/mp4; codecs="${codecString}"`,
          width: exportWidth,
          height: exportHeight,
          bitrate,
          framerate: fps
        }
      });
      console.log('[Export] MediaCapabilities probe', {
        type: mcType,
        codec: codecString,
        width: exportWidth,
        height: exportHeight,
        fps,
        bitrate,
        supported: info.supported,
        smooth: info.smooth,
        powerEfficient: info.powerEfficient
      });
      probed = true;
      break;
    } catch (err) {
      continue;
    }
  }
  if (!probed) {
    console.warn('[Export] MediaCapabilities probe failed (all types)');
  }
}

/**
 * Set up resolution change handler with tier check
 */
export function setupResolutionChangeHandler() {
  // Set up segmented control to sync with hidden select
  setupSegmentedControlSync(elements.resolutionControl, elements.resolutionSelect);

  previousResolution = elements.resolutionSelect.value;

  elements.resolutionSelect.addEventListener('change', async (e) => {
    const newResolution = e.target.value;

    // Check if user can use this resolution
    const canUse = await featureGate.canUseResolution(newResolution);
    if (!canUse) {
      const maxRes = await entitlementService.getMaxResolution();
      featureGate.showPaywall({
        feature: 'resolution',
        title: 'Upgrade Required',
        message: `${newResolution} resolution is available on Pro and Enterprise plans. Your current max is ${maxRes}.`,
        ctaText: 'Upgrade to Pro'
      });
      // Revert to previous resolution
      elements.resolutionSelect.value = previousResolution;
      // Also revert segmented control visual state
      syncSegmentedControlToSelect(elements.resolutionControl, previousResolution);
      return;
    }

    previousResolution = newResolution;
    const [width, height] = newResolution.split('x').map(Number);
    // Use setDimensions to track logical size (avoids pixel ratio scaling issues)
    managers.visualizerManager.setDimensions(width, height);
    // Also resize the lyrics overlay canvas
    elements.lyricsOverlayCanvas.width = width;
    elements.lyricsOverlayCanvas.height = height;
    // Reinitialize visualizer with new canvas size
    await managers.visualizerManager.setType(managers.visualizerManager.getType());
    buildControlsUI();
    // Recalculate lyrics scroll params for new canvas dimensions
    recalculateLyricsScrollParams();
    await probeCodecSupport();
  });
}

/**
 * Set up FPS change handler with tier check
 */
export function setupFpsChangeHandler() {
  // Set up segmented control to sync with hidden select
  setupSegmentedControlSync(elements.fpsControl, elements.fpsSelect);

  previousFps = elements.fpsSelect.value;

  elements.fpsSelect.addEventListener('change', async (e) => {
    const newFps = parseInt(e.target.value);

    // Check if user can use this FPS
    const canUse = await featureGate.canUseFps(newFps);
    if (!canUse) {
      featureGate.showPaywall({
        feature: 'fps',
        title: 'Upgrade Required',
        message: `${newFps} fps export is available on Pro and Enterprise plans.`,
        ctaText: 'Upgrade to Pro'
      });
      // Revert to previous FPS
      elements.fpsSelect.value = previousFps;
      // Also revert segmented control visual state
      syncSegmentedControlToSelect(elements.fpsControl, previousFps);
      return;
    }

    previousFps = e.target.value;
    await probeCodecSupport();
  });
}

/**
 * Set up codec and bitrate change handlers
 */
export function setupCodecBitrateHandlers() {
  // Set up bitrate segmented control
  setupSegmentedControlSync(elements.bitrateControl, elements.exportBitrateSelect);

  // Set up codec option cards (uses .option-card instead of .segment)
  if (elements.codecCards && elements.exportCodecSelect) {
    elements.codecCards.querySelectorAll('.option-card').forEach(card => {
      card.addEventListener('click', () => {
        // Update active state on all cards
        elements.codecCards.querySelectorAll('.option-card').forEach(c => c.classList.remove('active'));
        card.classList.add('active');

        // Update hidden select and trigger change event
        const value = card.dataset.value;
        if (elements.exportCodecSelect.value !== value) {
          elements.exportCodecSelect.value = value;
          elements.exportCodecSelect.dispatchEvent(new Event('change'));
        }
      });
    });
  }

  elements.exportCodecSelect.addEventListener('change', probeCodecSupport);
  elements.exportBitrateSelect.addEventListener('change', probeCodecSupport);
}

/**
 * Update export options with tier indicators (lock icons)
 */
export async function updateExportOptionsWithTiers() {
  const maxRes = await entitlementService.getMaxResolution();
  const allowedFps = await entitlementService.getAllowedFps();
  const [maxWidth] = maxRes.split('x').map(Number);

  // Update resolution options (hidden select)
  Array.from(elements.resolutionSelect.options).forEach(option => {
    const [width] = option.value.split('x').map(Number);
    const isLocked = width > maxWidth;
    const baseText = option.value === '1920x1080' ? '1920x1080 (1080p)' :
                     option.value === '1280x720' ? '1280x720 (720p)' :
                     option.value === '3840x2160' ? '3840x2160 (4K)' : option.value;
    option.textContent = isLocked ? `${baseText} 🔒` : baseText;
    option.dataset.locked = isLocked;
  });

  // Update resolution segmented control button styling for locked options
  if (elements.resolutionControl) {
    elements.resolutionControl.querySelectorAll('.segment').forEach(btn => {
      const [btnWidth] = btn.dataset.value.split('x').map(Number);
      const isLocked = btnWidth > maxWidth;
      btn.classList.toggle('locked', isLocked);
      btn.disabled = isLocked;
    });
  }

  // Set default to max allowed resolution
  const currentWidth = parseInt(elements.resolutionSelect.value.split('x')[0]);
  if (currentWidth > maxWidth) {
    // Current selection is locked, switch to max allowed
    const allowedOptions = Array.from(elements.resolutionSelect.options).filter(o => !o.dataset.locked || o.dataset.locked === 'false');
    if (allowedOptions.length > 0) {
      elements.resolutionSelect.value = allowedOptions[0].value;
      // Trigger change to update canvas
      elements.resolutionSelect.dispatchEvent(new Event('change'));
    }
  }

  // Sync resolution segmented control visual state with hidden select
  syncSegmentedControlToSelect(elements.resolutionControl, elements.resolutionSelect.value);

  // Update FPS options (hidden select)
  Array.from(elements.fpsSelect.options).forEach(option => {
    const fps = parseInt(option.value);
    const isLocked = !allowedFps.includes(fps);
    const baseText = `${fps} fps`;
    option.textContent = isLocked ? `${baseText} 🔒` : baseText;
    option.dataset.locked = isLocked;
  });

  // Update FPS segmented control button styling for locked options
  if (elements.fpsControl) {
    elements.fpsControl.querySelectorAll('.segment').forEach(btn => {
      const fps = parseInt(btn.dataset.value);
      const isLocked = !allowedFps.includes(fps);
      btn.classList.toggle('locked', isLocked);
      btn.disabled = isLocked;
    });
  }

  // Set default to allowed FPS
  const currentFps = parseInt(elements.fpsSelect.value);
  if (!allowedFps.includes(currentFps)) {
    elements.fpsSelect.value = allowedFps[0] || '30';
  }

  // Sync FPS segmented control visual state with hidden select
  syncSegmentedControlToSelect(elements.fpsControl, elements.fpsSelect.value);
}

/**
 * Set up export playlist summary listeners
 * Updates the summary when the export panel opens or playlist changes
 */
export function setupExportPlaylistSummary() {
  // Update when export panel opens
  document.addEventListener('panelOpened', (e) => {
    if (e.detail?.panel === 'export') {
      updateExportPlaylistSummary();
    }
  });

  // Update when playlist changes
  document.addEventListener('playlistUpdated', () => {
    updateExportPlaylistSummary();
  });

  // Initial update (in case export panel is somehow already visible)
  updateExportPlaylistSummary();
}

/**
 * Set up export button handler
 */
export function setupExportButton() {
  elements.exportBtn.addEventListener('click', async () => {
    // Handle stop request if export is running (render phase or pre-download phase)
    if (exportState.currentExporter) {
      exportState.exportWasAborted = true;
      exportState.currentExporter.abort();
      elements.exportBtn.disabled = true;
      elements.exportBtn.textContent = 'Stopping...';
      return;
    }
    if (exportState.isExporting) {
      // Pre-download phase: no exporter yet, but abort flag stops the download loop
      exportState.exportWasAborted = true;
      elements.exportBtn.disabled = true;
      elements.exportBtn.textContent = 'Stopping...';
      return;
    }

    // Reset abort flag for new export
    exportState.exportWasAborted = false;

    if (playlistState.playlist.length === 0) {
      alert('Please load audio files first');
      return;
    }

    const includedTracks = playlistState.playlist.filter(t => t.included);
    if (includedTracks.length === 0) {
      alert('Please select at least one track to include in export');
      return;
    }

    // Calculate total duration for export validation
    const totalDuration = includedTracks.reduce((sum, t) => sum + (t.duration || 0), 0);
    const resolution = elements.resolutionSelect.value;

    // Ensure a visualizer is ready before export (lazy initialization)
    // Export requires an active visualizer to get type and config
    await ensureVisualizerReady();

    const visualizerType = managers.visualizerManager.getType();

    // Validate export with entitlement service
    const fps = parseInt(elements.fpsSelect.value, 10);
    const validation = await entitlementService.validateExport({
      visualizerType,
      resolution,
      fps,
      durationSeconds: totalDuration,
      useCredits: false
    });

    if (!validation.allowed) {
      // Handle different rejection reasons
      if (validation.reason === 'visualizer_not_allowed') {
        const isPaidViz = await featureGate.isPaidTier();
        featureGate.showPaywall({
          feature: 'visualizer',
          title: 'Upgrade Required',
          message: validation.message,
          ctaText: isPaidViz ? 'Upgrade Plan' : 'View Plans'
        });
      } else if (validation.reason === 'resolution_exceeded') {
        const isPaid = await featureGate.isPaidTier();
        featureGate.showPaywall({
          feature: 'resolution',
          title: 'Upgrade Required',
          message: validation.message,
          ctaText: isPaid ? 'Upgrade Plan' : 'View Plans'
        });
      } else if (validation.reason === 'monthly_limit_exceeded') {
        const isPaid = await featureGate.isPaidTier();
        const isAuthenticated = featureGate.isAuthenticated();
        featureGate.showPaywall({
          feature: 'export_limit',
          title: 'Monthly Limit Reached',
          message: validation.message,
          ctaText: !isAuthenticated ? 'Sign Up Free' : (isPaid ? 'Upgrade Plan' : 'View Plans')
        });
      } else {
        alert(validation.message || 'Export not allowed');
      }
      return;
    }

    // Generate output filename
    const outputFilename = includedTracks.length === 1
      ? includedTracks[0].name.replace(/\.[^/.]+$/, '') + '_visualized.mp4'
      : includedTracks[0].name.replace(/\.[^/.]+$/, '') + '_playlist_visualized.mp4';

    // Check FSA support and prompt for save location first (requires user gesture)
    // DEV: Set window.FORCE_MEMORY_EXPORT = true to bypass FSA for performance testing
    let writableStream = null;
    let fileName = null;
    const tempExporter = new VideoExporter({});
    const useFSA = tempExporter.supportsFileSystemAccess() && !window.FORCE_MEMORY_EXPORT;

    if (window.FORCE_MEMORY_EXPORT) {
      console.log('[Export] DEV: Using in-memory mode (FORCE_MEMORY_EXPORT=true)');
    }

    if (useFSA) {
      try {
        const result = await tempExporter.promptForSaveLocation(outputFilename);
        writableStream = result.stream;
        fileName = result.fileName;
      } catch (error) {
        if (error.name === 'AbortError') {
          // User cancelled the file picker
          return;
        }
        // Fall back to in-memory mode
        console.warn('FSA failed, falling back to in-memory mode:', error);
      }
    }

    // Track export initiated
    trackExportInitiated({
      resolution,
      fps: parseInt(elements.fpsSelect.value),
      durationSeconds: Math.round(totalDuration),
      trackCount: includedTracks.length,
      visualizerType
    });

    // Set exporting flag to block all playback/UI interactions
    exportState.isExporting = true;
    exportState.exportStartTime = Date.now();

    // Switch to Stop button
    elements.exportBtn.textContent = 'Stop';
    elements.exportBtn.classList.remove('btn-primary');
    elements.exportBtn.classList.add('btn-danger');
    elements.exportProgress.style.display = 'block';

    // Reset and show the export status bar (hides transport bar)
    resetExportStatusBar();
    showExportStatusBar();

    // Cancel background duration loading to free main thread
    cancelDurationInitialization();

    // Exit live mode completely (if active) to free main thread and GPU
    // This stops mic stream, analyser, and animation loop
    const liveWasActive = uiState.isLiveMode;
    if (liveWasActive) {
      exitLiveMode();
    }

    // Pause regular playback (if active) to free main thread
    // This stops the playback animation loop that renders lyrics
    const playbackWasActive = playbackState.isPlaying;
    if (playbackWasActive) {
      pausePlayback();
    }

    // Run pre-export diagnostics to detect potential performance issues
    runPreExportDiagnostics();

    // Pause ALL UI animations to free GPU/compositor resources for export
    document.body.classList.add('export-active');  // Pauses CSS animations
    pausePlaylistAnimations();                      // Stops playlist scroll animation
    pauseThumbnailObserver();                       // Stops thumbnail lazy loading (potential WebGPU contention)
    const perfMonitorWasEnabled = perfMonitor.enabled;
    if (perfMonitorWasEnabled) {
      perfMonitor.stop();                           // Stops performance monitor intervals
    }

    // Suspend preview visualizer to free GPU resources for export
    // suspend() disposes the renderer entirely, unlike pause() which just stops the loop
    await managers.visualizerManager.suspend();

    // Clear preview canvases to ensure blank screen and release GPU resources
    // For WebGPU, suspend() replaces the canvas, but for 2D/WebGL the last frame persists
    const previewCanvas = managers.visualizerManager.getCanvas();
    if (previewCanvas) {
      const ctx = previewCanvas.getContext('2d');
      if (ctx) {
        ctx.fillStyle = '#000';
        ctx.fillRect(0, 0, previewCanvas.width, previewCanvas.height);
      }
    }
    // Clear lyrics overlay canvas as well
    if (elements.lyricsOverlayCanvas) {
      const lyricsCtx = elements.lyricsOverlayCanvas.getContext('2d');
      if (lyricsCtx) {
        lyricsCtx.clearRect(0, 0, elements.lyricsOverlayCanvas.width, elements.lyricsOverlayCanvas.height);
      }
    }

    // Show the export preview overlay to inform user why preview is disabled
    if (elements.exportPreviewOverlay) {
      elements.exportPreviewOverlay.classList.add('visible');
      // Initialize Lucide icon in the overlay
      if (window.lucide) {
        window.lucide.createIcons({ nodes: [elements.exportPreviewOverlay] });
      }
    }

    // Reset UI
    elements.progressBar.style.width = '0%';
    elements.progressText.textContent = '0%';
    elements.exportCurrentSong.textContent = 'Preparing...';
    elements.exportFileSize.textContent = '';
    elements.exportFpsInfo.textContent = '';
    elements.exportEtaInfo.textContent = '';

    // Show location if using FSA
    if (writableStream && fileName) {
      elements.exportLocation.textContent = `Saving to: ${fileName}`;
    } else {
      elements.exportLocation.textContent = 'Encoding to memory...';
    }

    try {
      // Pre-download audio for streamed (Suno) tracks before rendering
      const streamedTracks = includedTracks.filter(t => t.isStreamed && t.streamUrl && !t.audioBuffer && !t.rawAudioData);
      if (streamedTracks.length > 0) {
        elements.exportCurrentSong.textContent = 'Downloading audio...';
        updateExportStatusBar({ trackName: 'Downloading audio...' });

        await preDownloadStreamedAudio(includedTracks, {
          onProgress: (current, total) => {
            const msg = `Downloading audio ${current}/${total}...`;
            elements.exportCurrentSong.textContent = msg;
            updateExportStatusBar({ trackName: msg });
          },
          isAborted: () => exportState.exportWasAborted,
        });

        if (exportState.exportWasAborted) {
          elements.exportCurrentSong.textContent = 'Export cancelled';
          return;
        }

        elements.exportCurrentSong.textContent = 'Preparing export...';
        updateExportStatusBar({ trackName: 'Preparing export...' });
      }

      const fps = parseInt(elements.fpsSelect.value);
      const [exportWidth, exportHeight] = elements.resolutionSelect.value.split('x').map(Number);
      const exportCodec = elements.exportCodecSelect.value;
      const exportBitrate = parseInt(elements.exportBitrateSelect.value, 10);

      const exporter = new VideoExporter({
        width: exportWidth,
        height: exportHeight,
        fps: fps,
        videoCodecPreset: exportCodec,
        videoBitrate: exportBitrate,
        visualizerType: managers.visualizerManager.getType(),
        visualizerConfig: managers.visualizerManager.getConfig(),
        lyricsRenderer: managers.lyricsRenderer,
        lyricsEnabled: lyricsState.lyricsEnabled,
        onProgress: (progress) => {
          const percent = Math.round(progress * 100);
          elements.progressBar.style.width = percent + '%';
          elements.progressText.textContent = percent + '%';
          // Update status bar
          updateExportStatusBar({ progress });
        },
        onTrackChange: (name, current, total) => {
          // Strip extension from name for display
          const displayName = name.replace(/\.[^/.]+$/, '');
          const trackDisplay = total > 1
            ? `Track ${current}/${total}: ${displayName}`
            : displayName;
          elements.exportCurrentSong.textContent = total > 1
            ? `Track ${current}/${total}: ${displayName}`
            : `Rendering: ${displayName}`;
          // Update status bar
          updateExportStatusBar({ trackName: trackDisplay });
        },
        onBytesWritten: (bytes) => {
          const mb = (bytes / (1024 * 1024)).toFixed(1);
          elements.exportFileSize.textContent = `${mb} MB written`;
          // Update status bar
          updateExportStatusBar({ size: bytes });
        },
        onStatsUpdate: (fps, etaSeconds, currentFrame, totalFrames) => {
          elements.exportFpsInfo.textContent = `${fps.toFixed(1)} fps`;
          elements.exportEtaInfo.textContent = `ETA: ${formatEta(etaSeconds)}`;
          // Update status bar
          updateExportStatusBar({ fps, eta: etaSeconds });
        }
      });

      // Store reference for abort control
      exportState.currentExporter = exporter;

      await exporter.export(playlistState.playlist, {
        writableStream: writableStream,
        fileName: fileName
      });

      // Show completion message (only if not aborted)
      if (!exportState.exportWasAborted) {
        elements.exportCurrentSong.textContent = 'Export complete!';

        // Track export completed
        trackExportCompleted({
          fileSizeMb: exporter.totalBytesWritten ? (exporter.totalBytesWritten / (1024 * 1024)).toFixed(1) : null,
          exportDurationMs: Date.now() - exportState.exportStartTime,
          resolution: elements.resolutionSelect.value,
          fps: parseInt(elements.fpsSelect.value),
          visualizerType: managers.visualizerManager.getType()
        });
      } else {
        elements.exportCurrentSong.textContent = 'Export cancelled';
      }

    } catch (error) {
      console.error('Export error:', error);
      alert('Export failed: ' + error.message);
      // Close stream on error if it was opened
      if (writableStream) {
        try {
          await writableStream.abort();
        } catch (e) {
          console.warn('Failed to abort stream:', e);
        }
      }
    } finally {
      // Close/abort writableStream if still open (handles early return on abort
      // during pre-download phase where the exporter hasn't taken ownership)
      if (writableStream && !exportState.currentExporter) {
        try {
          await writableStream.abort();
        } catch (e) {
          console.warn('[Export] Failed to abort writableStream in finally:', e);
        }
      }

      // Clear exporter reference and exporting flag
      exportState.currentExporter = null;
      exportState.isExporting = false;

      // Release pre-downloaded audio data to free memory
      playlistState.playlist.forEach(t => { t.rawAudioData = null; });

      // Hide export status bar and show transport bar
      hideExportStatusBar();

      // Allow time for GPU resources to be fully released before restoring preview
      // VideoToolbox (macOS) and other hardware encoders may need significant time
      // to release sessions before new ones can be created efficiently
      const cleanupDelay = getPlatformCleanupDelay();
      console.log(`[Export] Waiting ${cleanupDelay}ms for GPU resources to be released...`);
      await new Promise(resolve => setTimeout(resolve, cleanupDelay));

      // Hide the export preview overlay before restoring
      if (elements.exportPreviewOverlay) {
        elements.exportPreviewOverlay.classList.remove('visible');
      }

      // Restore preview visualizer
      await managers.visualizerManager.restore();

      // Update UI to reflect the restored visualizer type
      // This ensures the controls panel and grid selection match the actual visualizer
      const restoredType = managers.visualizerManager.getType();
      if (restoredType) {
        buildControlsUI();
        updateVisualizerGridSelection(restoredType);
      }

      // Re-enter live mode if it was active before export
      if (liveWasActive) {
        enterLiveMode();
      }

      // Resume regular playback if it was active before export
      if (playbackWasActive) {
        startPlayback();
      }

      // Resume background duration loading if not complete
      resumeDurationInitialization();

      // Resume UI animations that were paused for export
      document.body.classList.remove('export-active');  // Resume CSS animations
      resumePlaylistAnimations();                        // Reset playlist scroll state
      resumeThumbnailObserver();                         // Re-enable thumbnail lazy loading
      if (perfMonitorWasEnabled) {
        perfMonitor.start();                             // Resume performance monitor
      }

      // Reset button to Export state
      elements.exportBtn.disabled = false;
      elements.exportBtn.textContent = 'Export Video';
      elements.exportBtn.classList.remove('btn-danger');
      elements.exportBtn.classList.add('btn-primary');

      // Keep progress visible briefly on completion for FSA mode
      if (writableStream) {
        setTimeout(() => {
          elements.exportProgress.style.display = 'none';
          elements.progressBar.style.width = '0%';
          elements.exportFpsInfo.textContent = '';
          elements.exportEtaInfo.textContent = '';
        }, TIMEOUT_CONFIG.progressHideDelay);
      } else {
        elements.exportProgress.style.display = 'none';
        elements.progressBar.style.width = '0%';
        elements.exportFpsInfo.textContent = '';
        elements.exportEtaInfo.textContent = '';
      }
    }
  });
}
